
import { ProcessDefinition, WorkflowProject, EngineProcess, EngineNode, EngineConnection, AppNode, EngineResource, WorkflowNodeData, EngineRecursiveConnection, Swimlane } from '../types';
import { Edge } from 'reactflow';
import { getEngineType, getExtensionHandler, getHandledFields, resolveEditorType } from './nodeRegistry';

// ID Manager to map UUIDs (Editor) <-> Integers (Engine)
class IdManager {
  private uuidToInt = new Map<string, number>();
  private intToUuid = new Map<number, string>();
  private nextId = 1;

  reset() {
      this.uuidToInt.clear();
      this.intToUuid.clear();
      this.nextId = 1;
  }

  register(uuid: string, id?: number): number {
    const intId = id !== undefined ? id : this.nextId++;
    if (id !== undefined && id >= this.nextId) this.nextId = id + 1;
    
    this.uuidToInt.set(uuid, intId);
    this.intToUuid.set(intId, uuid);
    return intId;
  }

  /**
   * Returns the engine integer id for a given editor uuid, registering a new
   * mapping on first sight. Named `getOrCreate*` because a miss mutates state;
   * the previous `getInt` name hid that side effect.
   */
  getOrCreateInt(uuid: string): number {
    if (!this.uuidToInt.has(uuid)) {
      return this.register(uuid);
    }
    return this.uuidToInt.get(uuid)!;
  }

  /**
   * Returns the editor uuid for a given engine integer id, fabricating a
   * stable uuid on first sight (used when loading engine JSON). Named
   * `getOrCreate*` because a miss mutates state.
   */
  getOrCreateUuid(id: number): string {
    if (!this.intToUuid.has(id)) {
        // Generate a stable UUID if loading from integer ID for the first time
        const newUuid = `node_${id}_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
        this.register(newUuid, id);
        return newUuid;
    }
    return this.intToUuid.get(id)!;
  }
}

const idManager = new IdManager();

// Type Mapping: Now uses registry via getEngineType()

// Helper to parse onException/retries which may be string (from select) or number (from import)
const toIntOrUndefined = (val: any): number | undefined => {
    if (val == null) return undefined;
    const n = Number(val);
    return isNaN(n) ? undefined : n;
};

const writeErrorHandling = (target: Record<string, any>, data: WorkflowNodeData) => {
    const onEx = toIntOrUndefined(data.onException);
    const retries = toIntOrUndefined(data.retries);
    if (onEx != null) target.OnException = onEx;
    if (retries != null) target.Retries = retries;
};

const readErrorHandling = (ext: any, data: WorkflowNodeData) => {
    // Store onException as string to match select option values ('0' | '1' | '2')
    // so conditional field rendering (e.g. retries shown when onException === '0')
    // works on first render without needing to re-select.
    if (typeof ext.Data?.OnException === 'number') data.onException = String(ext.Data.OnException);
    if (typeof ext.Data?.Retries === 'number') data.retries = ext.Data.Retries;
};

// Helper for Timer Kinds
const getTimerKind = (type?: string): number => {
    switch (type) {
        case 'date': return 2;
        case 'cycle': return 3;
        case 'cron': return 3; 
        default: return 1; // duration
    }
};

const getTimerTypeFromKind = (kind?: number): string => {
    switch (kind) {
        case 2: return 'date';
        case 3: return 'cycle';
        default: return 'duration';
    }
};

// Default edge stroke color used for sequence flows/boundary associations
// when restoring a diagram from the engine representation.
const DEFAULT_EDGE_STROKE = '#94a3b8';

// Lightweight local types that describe the editor-only / engine-only
// fields not covered by the public `Edge`/`EngineConnection`/`EngineProcess`
// interfaces. Using these avoids `as any` at call-sites.
type EdgeWithEditorData = Edge & { data?: { isRecursive?: boolean; expression?: string } };
type ConnectionWithDefaults = EngineConnection & { IsDefault?: boolean };
type RecursiveConnWithLabel = EngineRecursiveConnection & { ExpressionName?: string };
type EngineProcessWithExtensions = EngineProcess & { Extensions?: Array<{ Name?: string; Key?: string; Data?: string; Value?: string }> };

const INFERRED_LANE_PADDING_X = 80;
const INFERRED_LANE_PADDING_Y = 60;
const INFERRED_NODE_WIDTH = 220;
const INFERRED_NODE_HEIGHT = 100;
const INFERRED_BOUNDARY_SIZE = 56;
const INFERRED_BOUNDARY_LABEL_HEIGHT = 20;
const INFERRED_MIN_LANE_WIDTH = 240;
const INFERRED_MIN_LANE_HEIGHT = 120;

const estimateNodeSize = (node: AppNode): { width: number; height: number } => {
    if (node.type?.endsWith('BoundaryEvent')) {
        return {
            width: INFERRED_BOUNDARY_SIZE,
            height: INFERRED_BOUNDARY_SIZE + INFERRED_BOUNDARY_LABEL_HEIGHT,
        };
    }

    return {
        width: INFERRED_NODE_WIDTH,
        height: INFERRED_NODE_HEIGHT,
    };
};

const inferSingleLaneBoundsFromNodes = (nodes: AppNode[]): Pick<Swimlane, 'x' | 'y' | 'width' | 'height'> | null => {
    if (!Array.isArray(nodes) || nodes.length === 0) return null;

    const nodeById = new Map(nodes.map((node) => [node.id, node]));
    const absolutePositionCache = new Map<string, { x: number; y: number }>();

    const getAbsolutePosition = (node: AppNode): { x: number; y: number } => {
        const cached = absolutePositionCache.get(node.id);
        if (cached) return cached;

        const own = {
            x: Number(node.position?.x) || 0,
            y: Number(node.position?.y) || 0,
        };

        if (!node.parentNode) {
            absolutePositionCache.set(node.id, own);
            return own;
        }

        const parent = nodeById.get(node.parentNode);
        if (!parent) {
            absolutePositionCache.set(node.id, own);
            return own;
        }

        const parentAbs = getAbsolutePosition(parent);
        const absolute = {
            x: parentAbs.x + own.x,
            y: parentAbs.y + own.y,
        };
        absolutePositionCache.set(node.id, absolute);
        return absolute;
    };

    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    nodes.forEach((node) => {
        const position = getAbsolutePosition(node);
        const size = estimateNodeSize(node);

        minX = Math.min(minX, position.x);
        minY = Math.min(minY, position.y);
        maxX = Math.max(maxX, position.x + size.width);
        maxY = Math.max(maxY, position.y + size.height);
    });

    if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) {
        return null;
    }

    return {
        x: minX - INFERRED_LANE_PADDING_X,
        y: minY - INFERRED_LANE_PADDING_Y,
        width: Math.max(INFERRED_MIN_LANE_WIDTH, (maxX - minX) + INFERRED_LANE_PADDING_X * 2),
        height: Math.max(INFERRED_MIN_LANE_HEIGHT, (maxY - minY) + INFERRED_LANE_PADDING_Y * 2),
    };
};

// Boundary event types whose IsInterrupting flag is hardcoded true in the
// engine (see DiagramErrorBoundaryEventNode, DiagramTimerBoundaryEventNode).
// Exporting NonInterrupting variants for these types would fail to load.
const ALWAYS_INTERRUPTING_BOUNDARIES = new Set([
    'ErrorBoundaryEvent',
    'TimerBoundaryEvent',
]);

const mapToEngineType = (editorType: string, data: WorkflowNodeData): string => {
    // Handle Boundary Events Distinction
    if (editorType.endsWith('BoundaryEvent')) {
        if (ALWAYS_INTERRUPTING_BOUNDARIES.has(editorType)) {
            return getEngineType(editorType);
        }
        const isInterrupting = data.cancelActivity !== false;
        if (!isInterrupting) {
            return `NonInterrupting${editorType}`;
        }
    }
    return getEngineType(editorType);
}

const mapFromEngineType = (engineType: string, node: any): string => {
    // Handle Non-Interrupting Boundary Events (Engine -> Editor)
    if (engineType.startsWith('NonInterrupting')) {
        return engineType.replace('NonInterrupting', '');
    }

    // Check for Forms extension to decide if it's a FormStartEvent
    if (engineType === 'BlankStartEvent') {
        if (node.Extensions && node.Extensions.some((e: any) => e.Name === 'PSS.Forms')) {
            return 'FormStartEvent';
        }
        return 'BlankStartEvent';
    }

    // Try registry-based resolution (handles ServiceTask, etc.)
    if (node.Extensions && Array.isArray(node.Extensions)) {
        const resolved = resolveEditorType(engineType, node.Extensions);
        if (resolved) return resolved;
    }

    // Fallback for ServiceTask with no recognized extension
    if (engineType === 'ServiceTask') return 'ServiceTaskREST';

    return engineType;
};

// --- Extension Generators ---

const mapEditorTypeToValueType = (type: string): string => {
    switch (type) {
        case 'number': return 'long';
        case 'boolean': return 'boolean';
        case 'date': return 'date';
        case 'select': return 'enum';
        case 'email': return 'email';
        case 'guid': return 'guid';
        default: return 'string';
    }
};

const mapEditorTypeToInputType = (type: string): string => {
    switch (type) {
        case 'boolean': return 'checkbox';
        case 'select': return 'select';
        case 'date': return 'datepicker';
        case 'textarea': return 'textarea';
        default: return type;
    }
};

const createFormsExtension = (data: WorkflowNodeData) => {
    const formSchema = data.formSchema;
    if (!formSchema || !formSchema.fields) return null;

    // Merge field-level extensions with computed input type and group
    const buildExtensions = (f: any): Record<string, string> | undefined => {
        const ext: Record<string, string> = { ...f.extensions };
        ext.input = mapEditorTypeToInputType(f.type);
        if (f.group) ext.group = f.group;
        return Object.keys(ext).length > 0 ? ext : undefined;
    };

    return {
        Name: "PSS.Forms",
        Data: {
            Title: data.formTitle,
            Description: data.formDescription,
            Modifiers: data.formModifiers || '{\n\n}',
            FormFields: formSchema.fields.map((f: any) => ({
                Id: f.key || f.id,
                Label: f.label,
                Required: !!f.required,
                Disabled: !!f.disabled,
                Hidden: !!f.hidden,
                ValueType: f.valueType || mapEditorTypeToValueType(f.type),
                DefaultValue: f.defaultValue || undefined,
                Modifier: f.modifier || undefined,
                Values: f.values && Object.keys(f.values).length > 0 ? f.values : undefined,
                Extensions: buildExtensions(f)
            }))
        }
    };
};

const createStartProcessExtension = (data: WorkflowNodeData) => {
    const ext = data.startExtensions && Object.keys(data.startExtensions).length > 0 ? data.startExtensions : undefined;
    return {
        Name: "PSS.StartProcess",
        Data: {
            View: data.startView || "Workflow::GenericStartView",
            Roles: data.startRoles || "*",
            Step: data.startStep || "1",
            Deduplication: data.startDeduplication || "",
            Mode: data.startMode || "end",
            Extensions: ext,
        }
    };
};

const createUserTaskExtension = (data: WorkflowNodeData) => {
    const typeMap: Record<string, string> = {
        'generic': 'PSS.GenericUserTask',
        'produce': 'PSS.BadgeProduceTask',
        'smartbridge': 'PSS.SmartBridgeTask',
        'receipt': 'PSS.BadgeReceiptView'
    };
    
    const extName = typeMap[data.userTaskType || 'generic'] || 'PSS.GenericUserTask';
    
    const baseData: any = {
        Assignments: data.assignments || [],
        Mode: "continue",
        Step: data.userTaskStep ?? 1,
        Rejectable: data.userTaskRejectable ?? true,
        Subject: data.userTaskSubject || data.label,
        Object: data.userTaskObject || undefined,
        Modifiers: data.userTaskModifiers || undefined,
        Configuration: data.extensionConfig, // Early-Bound eval JSON script
        Relations: data.extensionRelations,   // Early-Bound eval JSON script
        Priority: data.priority // Moved from Editor.Properties
    };

    if (data.userTaskType === 'produce') {
        const operations: Record<string, string> = {};
        if (data.productionOperations && Array.isArray(data.productionOperations)) {
            data.productionOperations.forEach(op => {
                if (op.key) operations[op.key] = op.value;
            });
        }

        baseData.Production = {
            ProcessKey: data.productionProcessKey,
            Operations: operations
        };
    }

    if (data.userTaskType === 'smartbridge') {
        baseData.Job = data.jobScript; // Early-bound json script
    }

    // Error handling
    writeErrorHandling(baseData, data);

    // Legacy assignee support
    if ((!data.assignments || data.assignments.length === 0) && data.assignee) {
        baseData.AssigneeLegacy = data.assignee;
    }

    return {
        Name: extName,
        Data: baseData
    };
};

const createRestExtension = (data: WorkflowNodeData) => {
    // Store Auth/Headers in Extensions dictionary within the PSS Task
    const extensions: Record<string, any> = {};
    if (data.headers) extensions.Headers = data.headers;
    if (data.authentication) extensions.Authentication = data.authentication;
    if (data.bearerToken) extensions.BearerToken = data.bearerToken;
    if (data.apiKey) extensions.ApiKey = data.apiKey;

    const restData: Record<string, any> = {
        Topic: data.restTopic || "Query",
        Service: data.service,
        Endpoint: data.endpoint,
        Method: (data.method || 'GET').toUpperCase(),
        Body: data.requestBody,
        ResultVariable: data.resultVariable
    };

    writeErrorHandling(restData, data);
    if (Object.keys(extensions).length > 0) restData.Extensions = extensions;

    return {
        Name: "PSS.RestTask",
        Data: restData
    };
};

// Module-level helper — previously defined as a nested closure inside
// createEmailExtension for each of TO/CC/BCC. Extracted to avoid repeated
// deeply-nested closure creation and make the recipient builder testable.
const appendRecipients = (
    recipients: Array<{ Kind: string; Email: string; Name: string }>,
    kind: 'TO' | 'CC' | 'BCC',
    emailCsv: string | undefined,
    namesCsv: string | undefined,
): void => {
    if (!emailCsv) return;
    const emails = emailCsv.split(',').map((s: string) => s.trim());
    const names = (namesCsv || '').split(',').map((s: string) => s.trim());
    emails.forEach((email, i) => {
        recipients.push({ Kind: kind, Email: email, Name: names[i] || email });
    });
};

const createEmailExtension = (data: WorkflowNodeData) => {
    const recipients: Array<{ Kind: string; Email: string; Name: string }> = [];
    appendRecipients(recipients, 'TO', data.to, data.toNames);
    appendRecipients(recipients, 'CC', data.cc, data.ccNames);
    appendRecipients(recipients, 'BCC', data.bcc, data.bccNames);

    const emailData: Record<string, any> = {
        Topic: "Mail",
        SenderMail: data.fromEmail,
        SenderName: data.fromName,
        Receipients: recipients,
        Subject: data.subject,
        Content: data.body,
        IsHtml: data.isHtml,
        Attachments: data.attachments?.map((a: string) => ({ Url: a })) || []
    };
    writeErrorHandling(emailData, data);

    return { Name: "PSS.EmailTask", Data: emailData };
};

const createSmsExtension = (data: WorkflowNodeData) => {
    const smsData: Record<string, any> = {
        Topic: "SMS",
        Receiver: data.toPhone,
        Content: data.message
    };
    writeErrorHandling(smsData, data);

    return { Name: "PSS.SmsTask", Data: smsData };
};

const createDatabaseExtension = (data: WorkflowNodeData) => {
    const dbData: Record<string, any> = {
        Topic: "Database",
        ConnectionId: data.connectionId,
        QueryType: data.queryType,
        Query: data.query,
        ResultVariable: data.resultVariable,
        Timeout: data.timeout,
        Parameters: data.parameters // {Key, Value, Type} array
    };
    writeErrorHandling(dbData, data);

    return { Name: "PSS.DatabaseTask", Data: dbData };
};

const createMcpExtension = (data: WorkflowNodeData) => {
    const mcpData: Record<string, any> = {
        Topic: "MCP",
        ServerUrl: data.mcpServerUrl,
        ServerName: data.mcpServerName,
        Operation: data.mcpOperation,
        Headers: data.headers, // Reusing generic headers field
        ToolName: data.mcpToolName,
        ToolArguments: data.mcpToolArguments,
        ResourceUri: data.mcpResourceUri,
        PromptName: data.mcpPromptName,
        PromptArguments: data.mcpPromptArguments,
        Timeout: data.timeout,
        ResultVariable: data.resultVariable
    };
    writeErrorHandling(mcpData, data);

    return { Name: "PSS.McpTask", Data: mcpData };
};

const createAgentExtension = (data: WorkflowNodeData) => {
    const agentData: Record<string, any> = {
        Topic: "Agent",
        Model: data.model,
        SystemPrompt: data.systemPrompt,
        UserPrompt: data.userPrompt,
        OutputVariable: data.outputVariable
    };
    writeErrorHandling(agentData, data);

    return { Name: "PSS.AgentTask", Data: agentData };
};

const createDecisionExtension = (data: WorkflowNodeData) => {
    const decisionData: Record<string, any> = {
        Topic: "Command",
        Filename: data.decisionFile,
        InputMapping: data.inputMapping,
        ResultVariable: data.resultVariable,
        // Cache schema for simulation/variables
        OutputSchema: data._outputSchema
    };
    writeErrorHandling(decisionData, data);

    return { Name: "PSS.DecisionTask", Data: decisionData };
};

const createDataExtractionExtension = (data: WorkflowNodeData) => {
    const hasRules = data.extractionRules && data.extractionRules.length > 0;
    const hasUnpackAll = data.unpackAll === true;
    if (!hasRules && !hasUnpackAll && !data.exampleResponse) return null;

    return {
        Name: "PSS.DataExtraction",
        Data: {
            Example: data.exampleResponse,
            ...(hasUnpackAll ? { UnpackAll: true } : {}),
            Rules: (data.extractionRules || []).map(rule => ({
                Method: rule.method || 'jsonpath',
                Source: rule.source,
                Target: rule.targetVar,
                Type: rule.type || 'auto',
                ...(rule.defaultValue !== undefined && rule.defaultValue !== null && rule.defaultValue !== ''
                    ? { DefaultValue: rule.defaultValue }
                    : {})
            }))
        }
    };
};

// --- Extension Parsers ---

const mapEngineToEditorType = (valueType: string | undefined, inputType: string | undefined): string => {
    // Known standard input type mappings
    if (inputType === 'textarea') return 'textarea';
    if (inputType === 'select') return 'select';
    if (inputType === 'checkbox') return 'boolean';
    if (inputType === 'datepicker' || inputType === 'date') return 'date';
    if (inputType === 'email') return 'email';
    if (inputType === 'guid') return 'guid';

    // Custom/non-standard input types: pass through directly (e.g. "dateRange", "numeric")
    if (inputType && inputType !== 'text') return inputType;

    // No inputType or inputType is 'text': fall back to valueType-based inference
    if (valueType === 'boolean') return 'boolean';
    if (valueType === 'date') return 'date';
    if (valueType === 'email') return 'email';
    if (valueType === 'guid' || valueType === 'uuid') return 'guid';
    if (valueType === 'long' || valueType === 'number') return 'number';
    return 'text';
};

const parseStartProcessExtension = (ext: any, data: WorkflowNodeData) => {
    if (!ext.Data) return;
    data.startView = ext.Data.View;
    data.startRoles = ext.Data.Roles;
    data.startStep = ext.Data.Step;
    data.startDeduplication = ext.Data.Deduplication;
    data.startMode = ext.Data.Mode;
    if (ext.Data.Extensions) data.startExtensions = ext.Data.Extensions;
};

const parseFormsExtension = (ext: any, data: WorkflowNodeData) => {
    if (!ext.Data) return;

    data.formTitle = ext.Data.Title;
    data.formDescription = ext.Data.Description;
    if (ext.Data.Modifiers) data.formModifiers = ext.Data.Modifiers;

    if (ext.Data.FormFields) {
        data.formSchema = {
            fields: ext.Data.FormFields.map((f: any) => {
                // Separate known keys from pass-through extensions
                const { input, group, ...restExtensions } = f.Extensions || {};
                const hasRestExtensions = Object.keys(restExtensions).length > 0;

                return {
                    id: f.Id,
                    key: f.Id,
                    label: f.Label,
                    required: !!f.Required,
                    disabled: !!f.Disabled,
                    hidden: !!f.Hidden,
                    type: mapEngineToEditorType(f.ValueType, input),
                    valueType: f.ValueType || undefined,
                    defaultValue: f.DefaultValue || undefined,
                    modifier: f.Modifier || undefined,
                    group: group || undefined,
                    values: f.Values && Object.keys(f.Values).length > 0 ? f.Values : undefined,
                    extensions: hasRestExtensions ? restExtensions : undefined,
                };
            })
        };
    }
};

const parseUserTaskExtension = (ext: any, data: WorkflowNodeData) => {
    if (!ext.Data) return;
    
    if (ext.Name === 'PSS.GenericUserTask') data.userTaskType = 'generic';
    if (ext.Name === 'PSS.BadgeProduceTask') {
        data.userTaskType = 'produce';
        if (ext.Data.Production) {
            data.productionProcessKey = ext.Data.Production.ProcessKey;
            
            // Map Operations object { Key: Script } to Array [{key, value}]
            if (ext.Data.Production.Operations) {
                data.productionOperations = Object.entries(ext.Data.Production.Operations).map(([key, value]) => ({
                    key,
                    value: value as string
                }));
            } else {
                data.productionOperations = [];
            }
        }
    }
    if (ext.Name === 'PSS.SmartBridgeTask') {
        data.userTaskType = 'smartbridge';
        data.jobScript = ext.Data.Job;
    }
    if (ext.Name === 'PSS.BadgeReceiptView') data.userTaskType = 'receipt';

    data.assignments = ext.Data.Assignments || [];
    data.extensionConfig = ext.Data.Configuration;
    data.extensionRelations = ext.Data.Relations;

    // Engine properties
    if (ext.Data.Subject) data.userTaskSubject = ext.Data.Subject;
    if (ext.Data.Object) data.userTaskObject = ext.Data.Object;
    if (ext.Data.Modifiers) data.userTaskModifiers = ext.Data.Modifiers;
    if (typeof ext.Data.Step === 'number') data.userTaskStep = ext.Data.Step;
    if (typeof ext.Data.Rejectable === 'boolean') data.userTaskRejectable = ext.Data.Rejectable;

    if (ext.Data.AssigneeLegacy) data.assignee = ext.Data.AssigneeLegacy;
    if (ext.Data.Priority) data.priority = ext.Data.Priority;
    readErrorHandling(ext, data);
};

const parseRestExtension = (ext: any, data: WorkflowNodeData) => {
    if (!ext.Data) return;
    const reverseMethodMap = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];

    data.restTopic = ext.Data.Topic;
    data.service = ext.Data.Service;
    data.endpoint = ext.Data.Endpoint || ext.Data.EndPoint;
    data.method = typeof ext.Data.Method === 'number' ? reverseMethodMap[ext.Data.Method] || 'GET' : ext.Data.Method;
    readErrorHandling(ext, data);
    data.requestBody = ext.Data.Body;
    data.resultVariable = ext.Data.ResultVariable;
    
    if (ext.Data.Extensions) {
        data.headers = ext.Data.Extensions.Headers;
        data.authentication = ext.Data.Extensions.Authentication;
        data.bearerToken = ext.Data.Extensions.BearerToken;
        data.apiKey = ext.Data.Extensions.ApiKey;
    }
};

const parseEmailExtension = (ext: any, data: WorkflowNodeData) => {
    if (!ext.Data) return;
    data.fromEmail = ext.Data.SenderMail;
    data.fromName = ext.Data.SenderName;
    data.subject = ext.Data.Subject;
    data.body = ext.Data.Content;
    data.isHtml = ext.Data.IsHtml;
    
    if (ext.Data.Receipients) {
        data.to = ext.Data.Receipients.filter((r: any) => r.Kind === 'TO').map((r: any) => r.Email).join(', ');
        data.toNames = ext.Data.Receipients.filter((r: any) => r.Kind === 'TO').map((r: any) => r.Name).join(', ');
        data.cc = ext.Data.Receipients.filter((r: any) => r.Kind === 'CC').map((r: any) => r.Email).join(', ');
        data.ccNames = ext.Data.Receipients.filter((r: any) => r.Kind === 'CC').map((r: any) => r.Name).join(', ');
        data.bcc = ext.Data.Receipients.filter((r: any) => r.Kind === 'BCC').map((r: any) => r.Email).join(', ');
        data.bccNames = ext.Data.Receipients.filter((r: any) => r.Kind === 'BCC').map((r: any) => r.Name).join(', ');
    }
    
    if (ext.Data.Attachments) {
        data.attachments = ext.Data.Attachments.map((a: any) => a.Url);
    }
    readErrorHandling(ext, data);
};

const parseSmsExtension = (ext: any, data: WorkflowNodeData) => {
    if (!ext.Data) return;
    data.toPhone = ext.Data.Receiver;
    data.message = ext.Data.Content;
    readErrorHandling(ext, data);
};

const parseDatabaseExtension = (ext: any, data: WorkflowNodeData) => {
    if (!ext.Data) return;
    data.connectionId = ext.Data.ConnectionId;
    data.queryType = ext.Data.QueryType;
    data.query = ext.Data.Query;
    data.resultVariable = ext.Data.ResultVariable;
    data.timeout = ext.Data.Timeout;
    data.parameters = ext.Data.Parameters;
    readErrorHandling(ext, data);
};

const parseMcpExtension = (ext: any, data: WorkflowNodeData) => {
    if (!ext.Data) return;
    data.mcpServerUrl = ext.Data.ServerUrl;
    data.mcpServerName = ext.Data.ServerName;
    data.mcpOperation = ext.Data.Operation;
    data.headers = ext.Data.Headers;
    data.mcpToolName = ext.Data.ToolName;
    data.mcpToolArguments = ext.Data.ToolArguments;
    data.mcpResourceUri = ext.Data.ResourceUri;
    data.mcpPromptName = ext.Data.PromptName;
    data.mcpPromptArguments = ext.Data.PromptArguments;
    data.timeout = ext.Data.Timeout;
    data.resultVariable = ext.Data.ResultVariable;
    readErrorHandling(ext, data);
};

const parseAgentExtension = (ext: any, data: WorkflowNodeData) => {
    if (!ext.Data) return;
    data.model = ext.Data.Model;
    data.systemPrompt = ext.Data.SystemPrompt;
    data.userPrompt = ext.Data.UserPrompt;
    data.outputVariable = ext.Data.OutputVariable;
    readErrorHandling(ext, data);
};

const parseDecisionExtension = (ext: any, data: WorkflowNodeData) => {
    if (!ext.Data) return;
    data.decisionFile = ext.Data.Filename;
    data.inputMapping = ext.Data.InputMapping;
    data.resultVariable = ext.Data.ResultVariable;
    data._outputSchema = ext.Data.OutputSchema;
    readErrorHandling(ext, data);
};

const parseDataExtractionExtension = (ext: any, data: WorkflowNodeData) => {
    if (!ext.Data) return;
    data.exampleResponse = ext.Data.Example;
    if (ext.Data.UnpackAll === true) data.unpackAll = true;
    if (ext.Data.Rules && Array.isArray(ext.Data.Rules)) {
        data.extractionRules = ext.Data.Rules.map((r: any) => ({
            method: r.Method || (ext.Data.Format === 'xml' ? 'xpath' : ext.Data.Format === 'text' ? 'regex' : 'jsonpath'),
            source: r.Source,
            targetVar: r.Target,
            type: r.Type,
            ...(r.DefaultValue !== undefined && r.DefaultValue !== null ? { defaultValue: r.DefaultValue } : {})
        }));
    }
};

/**
 * Generates the Workflow Project object (PSS) from the current editor state.
 * This is the pure data generation part, suitable for both saving to file and passing to callbacks.
 */
export const generateWorkflowProject = (processes: ProcessDefinition[], projectName: string = "Workflow Project"): WorkflowProject => {
    const resources: Record<string, EngineResource> = {};

    const engineProcesses: EngineProcess[] = processes.map(proc => {
        idManager.reset();
        
        // 1. Register all nodes to ensure IDs are available for references
        proc.nodes.forEach(node => idManager.register(node.id));

        // 2. Process Nodes
        const engineNodes: EngineNode[] = proc.nodes.map(node => {
            const intId = idManager.getOrCreateInt(node.id);
            // Destructure to separate core properties and extension-handled data from generic editor data
            // Any property NOT listed here will end up in restData and subsequently in Editor.Properties
            const { 
                // Core Fields (shared across all node types)
                label, description, actorType,
                messageName, messageVariableName, messagePayloadVariableName, messagePayload, messageFilterScript,
                signalName, timerDefinition,
                script, 
                resultVariable, 
                isMerging, 
                exceptionType, 
                conditions, 
                outputs, 
                status, 
                
                // Form Fields (Handled by PSS.Forms)
                formSchema, formTitle, formDescription, formModifiers,
                startView, startRoles, startStep, startDeduplication, startMode, startExtensions,

                // User Task Fields (Handled by PSS.*UserTask)
                userTaskType, assignments, extensionConfig, extensionRelations,
                userTaskSubject, userTaskObject, userTaskModifiers, userTaskStep, userTaskRejectable,
                productionProcessKey, productionOperations, jobScript, assignee, priority,

                // REST Fields (Handled by PSS.RestTask)
                restTopic, service, endpoint, method, retries, onException, requestBody, headers, authentication, bearerToken, apiKey,

                // Email Fields (Handled by PSS.EmailTask)
                fromEmail, fromName, to, toNames, cc, ccNames, bcc, bccNames, subject, body, isHtml, attachments,

                // SMS Fields (Handled by PSS.SmsTask)
                toPhone, message,

                // DB Fields (Handled by PSS.DatabaseTask)
                connectionId, queryType, query, timeout, parameters,

                // LLM Fields (Handled by PSS.AgentTask)
                model, systemPrompt, userPrompt, outputVariable,

                // MCP Fields (Handled by PSS.McpTask)
                mcpServerUrl, mcpServerName, mcpOperation, mcpToolName, mcpToolArguments,
                mcpResourceUri, mcpPromptName, mcpPromptArguments,

                // Decision Fields (Handled by PSS.DecisionTask)
                decisionFile, inputMapping, _outputSchema,

                // Call Activity Fields
                processName, processCollection, processVersion, keepBusinessKey, sequentialLoop, collectionName, elementName, calledElement,

                // Extraction (Handled by PSS.DataExtraction)
                extractionRules, exampleResponse,

                // Script (Handled by EngineNode.Script Property)
                scriptLanguage,

                // Timer (Handled by EngineNode.Timer/Kind)
                timerType, timerDate, endDate, timezone,

                // Editor specific leftovers that should NOT be serialized
                isConnectableTarget, isBoundaryCandidate, isPotentialBoundaryTarget, categoryLabel,

                // Boundaries (Handled by type + Activity)
                cancelActivity,

                // Error Handling (ErrorEndEvent)
                errorMessage,
                errorPayload,

                // Everything else remains in restData for Editor.Properties
                ...restData 
            } = node.data;

            const engineType = mapToEngineType(node.type || 'Task', node.data);

            const extensions: any[] = [];

            // Remove registry-handled fields from restData (custom node extensions)
            const handledFields = getHandledFields(node.type || '');
            handledFields.forEach(f => delete restData[f]);

            // Add Editor.Properties only if there is residual data not covered by other extensions
            // or if we need to preserve specific UI state like custom colors (if implemented later)
            // Note: scriptLanguage is preserved here in restData
            if (Object.keys(restData).length > 0) {
                extensions.push({
                    Name: "Editor.Properties",
                    Data: JSON.stringify({
                        data: restData
                    })
                });
            }

            // --- Generate PSS Extensions ---
            if (node.data.formSchema) {
                const formsExt = createFormsExtension(node.data);
                if (formsExt) extensions.push(formsExt);

                if (node.type === 'FormStartEvent') {
                    extensions.push(createStartProcessExtension(node.data));
                }
            }

            // Built-in node types with special create functions
            if (node.type === 'UserTask') {
                const userExt = createUserTaskExtension(node.data);
                if (userExt) extensions.push(userExt);
            } else if (node.type === 'ServiceTaskREST') {
                extensions.push(createRestExtension(node.data));
            } else if (node.type === 'ServiceTaskEmail') {
                extensions.push(createEmailExtension(node.data));
            } else if (node.type === 'ServiceTaskSMS') {
                extensions.push(createSmsExtension(node.data));
            } else if (node.type === 'ServiceTaskDB') {
                extensions.push(createDatabaseExtension(node.data));
            } else if (node.type === 'ServiceTaskLLM') {
                extensions.push(createAgentExtension(node.data));
            } else if (node.type === 'ServiceTaskMCP') {
                extensions.push(createMcpExtension(node.data));
            } else if (node.type === 'ServiceTaskRules') {
                extensions.push(createDecisionExtension(node.data));
            } else {
                // Registry-based extension creation for custom nodes
                const handler = getExtensionHandler(node.type || '');
                if (handler) {
                    const ext = handler.create(node.data);
                    if (ext) extensions.push(ext);
                }
            }

            // Message filter script → PSS.MessageFilter extension
            const messageFilterTypes = ['MessageStartEvent', 'IntermediateCatchMessageEvent', 'MessageBoundaryEvent'];
            if (messageFilterTypes.includes(node.type || '') && node.data.messageFilterScript) {
                extensions.push({
                    Name: 'PSS.MessageFilter',
                    Fields: {
                        filterScript: node.data.messageFilterScript
                    }
                });
            }

            // Data Extraction can apply to any node type that returns data
            const extractionExt = createDataExtractionExtension(node.data);
            if (extractionExt) extensions.push(extractionExt);

            // Engine coordinates should ideally be absolute for generic tools
            let absoluteX = node.position.x;
            let absoluteY = node.position.y;
            if (node.parentNode) {
                const parent = proc.nodes.find(n => n.id === node.parentNode);
                if (parent) {
                    absoluteX += parent.position.x;
                    absoluteY += parent.position.y;
                }
            }

            // Base Node
            const engineNode: EngineNode = {
                type: engineType,
                Id: intId,
                Key: node.id, // Store string ID (Key) for persistence
                X: Math.round(absoluteX),
                Y: Math.round(absoluteY),
                Text: label,
                comment: description,
                Extensions: extensions
            };

            // actorType is a first-class EngineNode field — single source of
            // truth for swimlane linkage and task routing. Default to "Actor"
            // so every node has an explicit actor.
            engineNode.ActorType = (typeof actorType === 'string' && actorType.length > 0) ? actorType : 'Actor';

            // --- Property Mapping (Core Fields) ---
            if (node.data.messageName) {
                if (node.data.messageVariableName || node.data.messagePayloadVariableName || node.data.messagePayload) {
                    engineNode.Message = {
                        StaticText: node.data.messageName,
                        VariableName: node.data.messageVariableName,
                        PayloadVariableName: node.data.messagePayloadVariableName,
                        Payload: node.data.messagePayload,
                    };
                } else {
                    engineNode.Message = node.data.messageName;
                }
            }
            if (node.data.signalName) engineNode.Signal = node.data.signalName;
            
            // Map Timer + Type to Engine Node
            if (node.data.timerDefinition) {
                engineNode.Timer = node.data.timerDefinition;
                engineNode.Kind = getTimerKind(node.data.timerType);
            } else if (node.data.timerDate) {
                // If it's a date timer, put date in Timer string, set kind 2
                engineNode.Timer = node.data.timerDate;
                engineNode.Kind = 2; // Date
            }

            if (node.data.script) {
                engineNode.Script = node.data.script;
            }
            // Always write ResultVariable to engine node — the workflow engine reads it
            // from the service task directly, not from extensions
            if (node.data.resultVariable) {
                engineNode.ResultVariable = node.data.resultVariable;
            }

            // Call Activity Specifics
            if (node.type === 'CallActivity') {
                engineNode.ProcessName = {
                    Name: processName || '',
                    Collection: processCollection || '',
                    Version: processVersion || 0
                };
                engineNode.KeepBusinessKey = !!keepBusinessKey;
                engineNode.SequentialLoop = !!sequentialLoop;
                if (collectionName) engineNode.CollectionName = collectionName;
                if (elementName) engineNode.ElementName = elementName;
                // AsAsyncCall is hardcoded to false by the engine; do not export.
                engineNode.AsAsyncCall = false;
                // Also support input/output variables mapping if needed, usually done via Extensions or dedicated fields
                // For now, these might be in Editor.Properties or require a specific extension like 'PSS.CallActivity' if engine requires it
                // The prompt only asked for ProcessName and bool props on the node itself.
            }

            // SubProcess loop fields
            if (node.type === 'SubProcess') {
                engineNode.SequentialLoop = !!sequentialLoop;
                if (collectionName) engineNode.CollectionName = collectionName;
                if (elementName) engineNode.ElementName = elementName;
            }

            // Boundary Events - Attached to Activity
            if (node.type?.endsWith('BoundaryEvent')) {
                if (node.parentNode) {
                    engineNode.Activity = idManager.getOrCreateInt(node.parentNode);
                }
                
                // Set ErrorName and ErrorMessage for error boundaries
                if (node.type === 'ErrorBoundaryEvent') {
                    if (node.data.exceptionType) engineNode.ErrorName = node.data.exceptionType;
                    if (node.data.errorMessage) engineNode.ErrorMessage = node.data.errorMessage;
                }
            }
            
            // Activities - Boundary Events List
            const attachedBoundaries = proc.nodes.filter(n => n.parentNode === node.id);
            if (attachedBoundaries.length > 0) {
                engineNode.BoundaryEvents = attachedBoundaries.map(n => idManager.getOrCreateInt(n.id));
            }

            // Gateways
            if (node.type === 'XorGateway' || node.type === 'InclusiveGateway') {
                engineNode.IsMerging = !!node.data.isMerging;
                
                // Export DefaultPath logic
                // Implicit: Any path with an empty expression is the default path
                if (node.data.conditions && !engineNode.IsMerging) {
                    const defaultCondition = node.data.conditions.find((c: any) => !c.expression || c.expression.trim() === '');
                    if (defaultCondition) {
                         const defaultEdge = proc.edges.find(e => e.source === node.id && e.sourceHandle === String(defaultCondition.path));
                         if (defaultEdge) {
                             engineNode.DefaultPath = idManager.getOrCreateInt(defaultEdge.target);
                         }
                    }
                }
                
                if (node.data.conditions) {
                    engineNode.Expressions = {};
                    engineNode.ExpressionMapping = {};
                    
                    const outgoingEdges = proc.edges.filter(e => e.source === node.id);
                    
                    node.data.conditions.forEach((cond: any, index: number) => {
                        // Use output label as expression name if valid, else cond_N
                        let exprName = cond.label || `cond_${index}`;

                        // Use label from outputs array if available (preferred)
                        // FIX: Use cond.path instead of index for outputs lookup
                        if (node.data.outputs && node.data.outputs[cond.path]) {
                            exprName = node.data.outputs[cond.path];
                        }

                        if (cond.expression && cond.expression.trim() !== '') {
                            if (!engineNode.Expressions) engineNode.Expressions = {};
                            engineNode.Expressions[exprName] = cond.expression;

                            // FIX: Only add to ExpressionMapping for non-default conditions
                            const edge = outgoingEdges.find(e => e.sourceHandle === String(cond.path));
                            if (edge) {
                                const targetId = String(idManager.getOrCreateInt(edge.target));
                                if (!engineNode.ExpressionMapping) engineNode.ExpressionMapping = {};
                                engineNode.ExpressionMapping[targetId] = exprName;
                            }
                        }
                        // Default conditions (empty expression) are handled by DefaultPath — not in ExpressionMapping
                    });
                }
            }
            
            if (node.type === 'ParallelGateway') {
                engineNode.IsMerging = !!node.data.isMerging;
            }

            // Error End Event
            if (node.type === 'ErrorEndEvent') {
                if (errorMessage) engineNode.Message = errorMessage;
                if (errorPayload) engineNode.Error = errorPayload;
            }

            return engineNode;
        });

        // 3. Process Connections
        const engineConnections: EngineConnection[] = [];
        const engineRecursiveConnections: EngineRecursiveConnection[] = [];
        const connectionSet = new Set<string>();

        proc.edges.forEach(edge => {
            const targetNode = proc.nodes.find(n => n.id === edge.target);
            if (targetNode?.type?.endsWith('BoundaryEvent') && targetNode.parentNode === edge.source) {
                return;
            }

            const fromId = idManager.getOrCreateInt(edge.source);
            const toId = idManager.getOrCreateInt(edge.target);

            // Deduplicate: same From + To
            const dedupeKey = `${fromId}-${toId}`;
            if (connectionSet.has(dedupeKey)) return;
            connectionSet.add(dedupeKey);

            // Separate recursive edges from regular connections
            if ((edge as EdgeWithEditorData).data?.isRecursive) {
                engineRecursiveConnections.push({ From: fromId, To: toId });
                return;
            }

            const sourceNode = proc.nodes.find(n => n.id === edge.source);

            const conn: EngineConnection = {
                From: fromId,
                To: toId,
                StartPoint: 1,
                EndPoint: 3,
                IsDefault: false
            };

            // Determine IsDefault and ExpressionName from source node condition data
            if (sourceNode && (sourceNode.type === 'XorGateway' || sourceNode.type === 'InclusiveGateway')) {
                const conds = sourceNode.data.conditions || [];
                const matchingCond = conds.find((c: any) => String(c.path) === edge.sourceHandle);
                if (matchingCond && (!matchingCond.expression || matchingCond.expression.trim() === '')) {
                    conn.IsDefault = true;
                } else if (matchingCond && matchingCond.expression && matchingCond.expression.trim() !== '') {
                    const outputs = sourceNode.data.outputs || [];
                    conn.ExpressionName = outputs[matchingCond.path] || (edge.label as string) || '';
                }
            } else {
                // Non-gateway: use edge label if present
                const hasExpression = edge.label && typeof edge.label === 'string' && edge.label.trim() !== '';
                if (hasExpression) {
                    conn.ExpressionName = edge.label as string;
                }
            }

            engineConnections.push(conn);
        });

        // Editor-only process-level extensions. Swimlanes are pure geometry
        // (id, name, x, y, width, height, color) — stored here so the engine
        // never sees them as nodes; linkage to nodes is via node.actorType.
        const processExtensions: any[] = [];
        if (proc.swimlanes && proc.swimlanes.length > 0) {
            processExtensions.push({
                Name: 'Editor.Swimlanes',
                Data: JSON.stringify({ version: 1, lanes: proc.swimlanes }),
            });
        }

        const engineProc: EngineProcess = {
            Name: proc.name,
            IsSubProcess: !!proc.isSubProcess,
            Nodes: engineNodes,
            Connections: engineConnections,
            RecursiveConnections: engineRecursiveConnections,
        };
        if (processExtensions.length > 0) engineProc.Extensions = processExtensions;
        return engineProc;
    });

    return {
        Name: projectName,
        Version: 1,
        Resources: resources,
        Processes: engineProcesses
    };
};

/**
 * Parses a Workflow Project object (PSS) into Editor Process Definitions.
 */
export const parseWorkflowProject = (project: WorkflowProject): ProcessDefinition[] => {
    return project.Processes.map((proc, procIndex) => {
        idManager.reset();
        const engineNodeMap = new Map<number, EngineNode>();
        const childToParentMap = new Map<number, number>();

        // Map to store gateway logic for later edge connection
        // Map<SourceEngineId, Map<TargetEngineId, HandleIndexString>>
        const gatewayEdgeMap = new Map<number, Map<number, string>>();
        // Map<SourceEngineId, Map<TargetEngineId, OutputLabel>> for edge labels
        const gatewayLabelMap = new Map<number, Map<number, string>>();

        if (proc.Nodes) {
            proc.Nodes.forEach(n => {
                engineNodeMap.set(n.Id, n);
                if (n.Activity) childToParentMap.set(n.Id, n.Activity);
                if (n.BoundaryEvents) n.BoundaryEvents.forEach(childId => childToParentMap.set(childId, n.Id));
            });
        }

        const nodes: AppNode[] = (proc.Nodes || []).map((node) => {
            let editorData: any = null;
            let editorType: string | null = null;
            let savedPosition: { x: number, y: number } | null = null;
            
            // 1. Try to restore exact editor state from Editor.Properties if available
            if (node.Extensions && Array.isArray(node.Extensions)) {
                const ext = node.Extensions.find((e: any) => (e.Name || e.Key) === 'Editor.Properties');
                if (ext && (ext.Data || ext.Value)) {
                    try {
                        const parsed = JSON.parse(ext.Data || ext.Value);
                        editorData = parsed.data;
                        editorType = parsed.type;
                        savedPosition = parsed.position;
                    } catch (_e) { /* ignore parse errors */ }
                }
            }

            // Prefer Key if available (it contains the original string ID), otherwise generate stable UUID from Integer ID
            let uuid = node.Key;
            if (!uuid) {
                uuid = idManager.getOrCreateUuid(node.Id);
            }
            idManager.register(uuid, node.Id);

            // 2. If no editor state, or to ensure sync with engine state, parse PSS Extensions
            if (!editorData) {
                editorData = {};
            }
            
            // Always default status to idle on load, as status is transient
            if (!editorData.status) {
                editorData.status = 'idle';
            }

            // Restore core properties that were stripped from Editor.Properties
            if (!editorData.label && node.Text) editorData.label = node.Text;
            if (!editorData.description && node.comment) editorData.description = node.comment;

            // Determine Type if not set by Editor.Properties
            if (!editorType) {
                editorType = mapFromEngineType(node.type, node);
            }

            // Apply extensions overwriting any stale Editor.Properties
            if (node.Extensions) {
                node.Extensions.forEach(ext => {
                    // Built-in extension parsers
                    if (ext.Name === 'PSS.Forms') parseFormsExtension(ext, editorData);
                    if (ext.Name === 'PSS.StartProcess') parseStartProcessExtension(ext, editorData);
                    if (ext.Name === 'PSS.RestTask') parseRestExtension(ext, editorData);
                    if (ext.Name === 'PSS.EmailTask') parseEmailExtension(ext, editorData);
                    if (ext.Name === 'PSS.SmsTask') parseSmsExtension(ext, editorData);
                    if (ext.Name === 'PSS.DatabaseTask') parseDatabaseExtension(ext, editorData);
                    if (ext.Name === 'PSS.McpTask') parseMcpExtension(ext, editorData);
                    if (ext.Name === 'PSS.AgentTask') parseAgentExtension(ext, editorData);
                    if (ext.Name === 'PSS.DecisionTask') parseDecisionExtension(ext, editorData);
                    if (ext.Name === 'PSS.DataExtraction') parseDataExtractionExtension(ext, editorData);
                    if (ext.Name === 'PSS.MessageFilter') editorData.messageFilterScript = ext.Fields?.filterScript || '';

                    if (['PSS.GenericUserTask', 'PSS.BadgeProduceTask', 'PSS.SmartBridgeTask', 'PSS.BadgeReceiptView'].includes(ext.Name)) {
                        parseUserTaskExtension(ext, editorData);
                    }

                    // Registry-based extension parsers (custom nodes)
                    if (editorType && ext.Name) {
                        const handler = getExtensionHandler(editorType);
                        if (handler && handler.name === ext.Name) {
                            handler.parse(ext, editorData);
                        }
                    }
                });
            }

            // actorType is a first-class EngineNode field — read it directly.
            if (typeof node.ActorType === 'string' && node.ActorType.length > 0) {
                editorData.actorType = node.ActorType;
            }

            // Handle Non-Interrupting flag based on original engine type if not set
            if (node.type.startsWith('NonInterrupting')) {
                editorData.cancelActivity = false;
            } else if (editorType?.endsWith('BoundaryEvent') && editorData.cancelActivity === undefined) {
                // If it is a boundary event and NOT non-interrupting engine type, 
                // ensure cancelActivity is true (default)
                editorData.cancelActivity = true;
            }

            // Gateway Logic Reconstruction
            // Since we stripped conditions/outputs from Editor.Properties, we MUST reconstruct them here.
            // ParallelGateway fans out unconditionally — no conditions/outputs. Only XOR/Inclusive
            // branch by expression, so they're the only types that need reconstructed outputs.
            if (editorType === 'XorGateway' || editorType === 'InclusiveGateway' || editorType === 'ParallelGateway') {
                editorData.isMerging = !!node.IsMerging;

                if (!node.IsMerging && editorType !== 'ParallelGateway') {
                    // Find outgoing connections for this node (include recursive connections)
                    const outgoing = [
                        ...(proc.Connections || []).filter(c => c.From === node.Id),
                        ...(proc.RecursiveConnections || []).filter(c => c.From === node.Id),
                    ];
                    
                    const outputs: string[] = [];
                    const conditions: any[] = [];
                    const targetToHandle = new Map<number, string>();
                    const targetToLabel = new Map<number, string>();

                    outgoing.forEach((conn, idx) => {
                        const targetId = conn.To;
                        const handleId = String(idx);
                        targetToHandle.set(targetId, handleId);

                        // Determine label and expression
                        let label = `Path ${idx + 1}`;
                        let expression = "";

                        const targetIdStr = String(targetId);
                        const connAny = conn as ConnectionWithDefaults & { ExpressionName?: string };

                        // Check ExpressionMapping
                        if (node.ExpressionMapping && node.ExpressionMapping[targetIdStr]) {
                            const exprName = node.ExpressionMapping[targetIdStr];
                            label = exprName;
                            if (node.Expressions && node.Expressions[exprName]) {
                                expression = node.Expressions[exprName];
                            }
                        }
                        // Check Default Path - name it "default" so it's visible in the editor
                        else if (node.DefaultPath === targetId || connAny.IsDefault) {
                            label = "default";
                        }
                        // Check Connection ExpressionName (legacy/alternative)
                        else if (connAny.ExpressionName) {
                            label = connAny.ExpressionName;
                            // Attempt to see if this name maps to an expression code, otherwise leave empty (true)
                            if (node.Expressions && node.Expressions[label]) {
                                expression = node.Expressions[label];
                            }
                        }

                        outputs.push(label);
                        targetToLabel.set(targetId, label);

                        // Add condition entry - default paths get empty expression
                        if (expression && expression.trim() !== '') {
                            conditions.push({ path: idx, label, expression });
                        } else {
                            // Default path: add condition with empty expression so it appears in the editor
                            conditions.push({ path: idx, label, expression: '' });
                        }
                    });

                    editorData.outputs = outputs;
                    editorData.conditions = conditions;
                    if (node.IsMerging !== undefined) editorData.isMerging = node.IsMerging;
                    
                    if (outgoing.length > 0) {
                        gatewayEdgeMap.set(node.Id, targetToHandle);
                        gatewayLabelMap.set(node.Id, targetToLabel);
                    }
                }
            }

            // Parse Core Properties (Fallbacks)
            if (node.Script && typeof node.Script === 'string') {
                // Directly use script content from the node
                editorData.script = node.Script;
                // If scriptLanguage wasn't preserved in editorData, default it
                if (!editorData.scriptLanguage) {
                    editorData.scriptLanguage = 'javascript';
                }
            }
            if (node.Message) {
                if (typeof node.Message === 'string') {
                    editorData.messageName = node.Message;
                } else {
                    editorData.messageName = node.Message.StaticText;
                    editorData.messageVariableName = node.Message.VariableName;
                    editorData.messagePayloadVariableName = node.Message.PayloadVariableName;
                    editorData.messagePayload = node.Message.Payload;
                }
            }
            if (node.Signal) editorData.signalName = node.Signal;
            
            if (node.Timer) {
                editorData.timerDefinition = node.Timer;
                // Infer timer type based on Kind if present, else fallback logic
                if (node.Kind) {
                    editorData.timerType = getTimerTypeFromKind(node.Kind);
                    // Special case for Date: if type is date, the definition IS the date
                    if (editorData.timerType === 'date') {
                        editorData.timerDate = node.Timer;
                    }
                } else if (!editorData.timerType) {
                    // Legacy Fallback
                    const t = node.Timer.trim();
                    if (t.startsWith('R')) editorData.timerType = 'cycle';
                    else if (t.includes(' ') || t.includes('*')) editorData.timerType = 'cron';
                    else if (t.startsWith('P') || t.startsWith('-P')) editorData.timerType = 'cycle'; // Map ISO duration to cycle
                    else if (!isNaN(Date.parse(t)) && t.includes('T')) {
                        editorData.timerType = 'date';
                        editorData.timerDate = t;
                    } else {
                        editorData.timerType = 'cycle'; // Default fallback
                    }
                }
            }

            if (node.ResultVariable && !editorData.resultVariable) editorData.resultVariable = node.ResultVariable;
            if (node.IsMerging !== undefined) editorData.isMerging = node.IsMerging;
            
            if (editorType === 'ErrorBoundaryEvent') {
                if (node.ErrorName) editorData.exceptionType = node.ErrorName;
                if (node.ErrorMessage) editorData.errorMessage = node.ErrorMessage;
            }

            if (editorType === 'ErrorEndEvent') {
                if (node.Message && typeof node.Message === 'string') {
                    editorData.errorMessage = node.Message;
                }
                if (node.Error) {
                    editorData.errorPayload = node.Error;
                }
            }

            // Call Activity Parsing
            if (editorType === 'CallActivity') {
                if (node.ProcessName) {
                    editorData.processName = node.ProcessName.Name;
                    editorData.processCollection = node.ProcessName.Collection;
                    editorData.processVersion = node.ProcessName.Version;
                }
                if (node.KeepBusinessKey !== undefined) editorData.keepBusinessKey = node.KeepBusinessKey;
                if (node.SequentialLoop !== undefined) editorData.sequentialLoop = node.SequentialLoop;
                if (node.CollectionName !== undefined) editorData.collectionName = node.CollectionName;
                if (node.ElementName !== undefined) editorData.elementName = node.ElementName;
                // AsAsyncCall intentionally dropped on import: engine hardcodes to false.
            }

            // SubProcess loop fields
            if (editorType === 'SubProcess') {
                if (node.SequentialLoop !== undefined) editorData.sequentialLoop = node.SequentialLoop;
                if (node.CollectionName !== undefined) editorData.collectionName = node.CollectionName;
                if (node.ElementName !== undefined) editorData.elementName = node.ElementName;
            }

            const parentId = childToParentMap.get(node.Id);
            let parentUuid: string | undefined = undefined;
            if (parentId !== undefined && parentId !== node.Id && engineNodeMap.has(parentId)) {
                parentUuid = idManager.getOrCreateUuid(parentId);
            }

            // 3. Coordinate Handling
            let position = savedPosition || { x: node.X || 0, y: node.Y || 0 };
            
            if (!savedPosition && parentUuid && parentId) {
                const parent = engineNodeMap.get(parentId);
                if (parent) {
                    position = {
                        x: (node.X || 0) - (parent.X || 0),
                        y: (node.Y || 0) - (parent.Y || 0)
                    };
                }
            }

            return {
                id: uuid,
                type: editorType || 'UserTask',
                position,
                data: editorData,
                parentNode: parentUuid,
                zIndex: editorType?.endsWith('BoundaryEvent') ? 2000 : undefined,
                extent: (parentUuid && !editorType?.endsWith('BoundaryEvent')) ? 'parent' : undefined
            };
        });
        
        nodes.sort((a, b) => {
            const aHasParent = !!a.parentNode;
            const bHasParent = !!b.parentNode;
            if (aHasParent === bHasParent) return 0;
            return aHasParent ? 1 : -1;
        });

        // RecursiveConnections are annotations on existing Connections, not
        // separate traversal edges. Build a set of From->To pairs that are
        // flagged recursive so we can mark the matching regular edge.
        const recursivePairs = new Set<string>();
        (proc.RecursiveConnections || []).forEach((conn) => {
            recursivePairs.add(`${conn.From}->${conn.To}`);
        });

        const edges: Edge[] = (proc.Connections || []).map((conn, connIdx) => {
            const source = idManager.getOrCreateUuid(conn.From);
            const target = idManager.getOrCreateUuid(conn.To);

            // Determine sourceHandle based on Gateway mapping if available
            let sourceHandle = null;
            if (gatewayEdgeMap.has(conn.From)) {
                const mapping = gatewayEdgeMap.get(conn.From);
                if (mapping?.has(conn.To)) {
                    sourceHandle = mapping.get(conn.To);
                }
            }

            // Use ExpressionName if available, fall back to gateway output label
            let edgeLabel = conn.ExpressionName;
            if (!edgeLabel && gatewayLabelMap.has(conn.From)) {
                edgeLabel = gatewayLabelMap.get(conn.From)?.get(conn.To);
            }

            const isRecursive = recursivePairs.has(`${conn.From}->${conn.To}`);

            return {
                id: `e_${source}_${target}_${connIdx}`,
                source,
                target,
                sourceHandle: sourceHandle || null,
                label: edgeLabel,
                animated: true,
                style: { stroke: DEFAULT_EDGE_STROKE },
                type: 'deletable',
                data: isRecursive ? { isRecursive: true } : undefined,
            } as Edge;
        });

        // Handle orphan RecursiveConnections — entries that lack a matching
        // regular Connection. These shouldn't exist per engine semantics
        // (rule 2.6) but we still render them so the editor can flag them.
        const connectionPairs = new Set(
            (proc.Connections || []).map(c => `${c.From}->${c.To}`)
        );
        (proc.RecursiveConnections || []).forEach((conn, idx) => {
            const key = `${conn.From}->${conn.To}`;
            if (connectionPairs.has(key)) return;

            const source = idManager.getOrCreateUuid(conn.From);
            const target = idManager.getOrCreateUuid(conn.To);

            let sourceHandle = null;
            if (gatewayEdgeMap.has(conn.From)) {
                const mapping = gatewayEdgeMap.get(conn.From);
                if (mapping?.has(conn.To)) {
                    sourceHandle = mapping.get(conn.To);
                }
            }

            let edgeLabel = (conn as RecursiveConnWithLabel).ExpressionName;
            if (!edgeLabel && gatewayLabelMap.has(conn.From)) {
                edgeLabel = gatewayLabelMap.get(conn.From)?.get(conn.To);
            }

            edges.push({
                id: `e_rec_${source}_${target}_${idx}`,
                source,
                target,
                sourceHandle: sourceHandle || null,
                label: edgeLabel,
                animated: true,
                style: { stroke: DEFAULT_EDGE_STROKE },
                type: 'deletable',
                data: { isRecursive: true },
            });
        });

        nodes.forEach(node => {
            if (node.type?.endsWith('BoundaryEvent') && node.parentNode) {
                 const parent = nodes.find(n => n.id === node.parentNode);
                 if (parent) {
                     const edgeId = `e_assoc_${parent.id}_${node.id}`;
                     edges.push({
                         id: edgeId,
                         source: parent.id,
                         target: node.id,
                         sourceHandle: 'boundary',
                         targetHandle: null,
                         type: 'deletable',
                         animated: false,
                         style: { stroke: DEFAULT_EDGE_STROKE }, 
                         selected: false
                     });
                 }
            }
        });

        // Restore editor-only swimlane geometry from process Extensions.
        // Invalid / missing → undefined (no lanes).
        let swimlanes: Swimlane[] | undefined;
        let singleRawLane: any | undefined;
        const procWithExt = proc as EngineProcessWithExtensions;
        if (Array.isArray(procWithExt.Extensions)) {
            const ext = procWithExt.Extensions.find(
                (e) => (e?.Name || e?.Key) === 'Editor.Swimlanes'
            );
            if (ext && (ext.Data || ext.Value)) {
                try {
                    const parsed = JSON.parse(ext.Data || ext.Value);
                    const lanes = Array.isArray(parsed?.lanes) ? parsed.lanes : parsed;
                    if (Array.isArray(lanes)) {
                        const validLanes = lanes.filter((l: any) => l && typeof l.name === 'string');
                        if (validLanes.length === 1) singleRawLane = validLanes[0];
                        swimlanes = validLanes
                            .map((l: any) => ({
                                id: String(l.id ?? `lane_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`),
                                name: String(l.name),
                                x: Number.isFinite(Number(l.x)) ? Number(l.x) : 0,
                                y: Number.isFinite(Number(l.y)) ? Number(l.y) : 0,
                                width: Number.isFinite(Number(l.width)) ? Number(l.width) : 1600,
                                height: Number.isFinite(Number(l.height)) ? Number(l.height) : 240,
                                color: typeof l.color === 'string' ? l.color : undefined,
                            })) as Swimlane[];

                    }
                } catch (_e) { /* ignore parse errors */ }
            }
        }

        const actorNames = new Set<string>();
        nodes.forEach((node) => {
            const actorType = node.data?.actorType;
            if (typeof actorType === 'string' && actorType.trim()) {
                actorNames.add(actorType.trim());
            }
        });
        (swimlanes || []).forEach((lane) => {
            if (typeof lane.name === 'string' && lane.name.trim()) {
                actorNames.add(lane.name.trim());
            }
        });

        if (actorNames.size === 1) {
            const [actorName] = Array.from(actorNames);
            const existingLane = swimlanes && swimlanes.length === 1 ? swimlanes[0] : undefined;
            const inferredBounds = inferSingleLaneBoundsFromNodes(nodes);

            if (existingLane) {
                existingLane.name = actorName;
                const rawLaneHasExplicitGeometry =
                    Number.isFinite(Number(singleRawLane?.x)) &&
                    Number.isFinite(Number(singleRawLane?.y)) &&
                    Number.isFinite(Number(singleRawLane?.width)) &&
                    Number.isFinite(Number(singleRawLane?.height));

                if (!rawLaneHasExplicitGeometry && inferredBounds) {
                    existingLane.x = inferredBounds.x;
                    existingLane.y = inferredBounds.y;
                    existingLane.width = inferredBounds.width;
                    existingLane.height = inferredBounds.height;
                }
                swimlanes = [existingLane];
            } else if (inferredBounds) {
                swimlanes = [{
                    id: `lane_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                    name: actorName,
                    x: inferredBounds.x,
                    y: inferredBounds.y,
                    width: inferredBounds.width,
                    height: inferredBounds.height,
                }];
            } else {
                swimlanes = [{
                    id: `lane_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                    name: actorName,
                    x: 0,
                    y: 0,
                    width: 240,
                    height: 120,
                }];
            }

            nodes.forEach((node) => {
                node.data = {
                    ...node.data,
                    actorType: actorName,
                };
            });
        }

        return {
            id: `proc_${Date.now()}_${procIndex}`,
            name: proc.Name || `Imported Process ${procIndex + 1}`,
            isSubProcess: !!proc.IsSubProcess,
            description: "",
            nodes,
            edges,
            swimlanes,
        };
    });
};

/**
 * Loads a project from a JSON file, strictly adhering to Unified Engine Schema.
 */
export const loadProjectFromFile = async (file: File): Promise<ProcessDefinition[]> => {
    let text = "";
    try {
        text = await file.text();
    } catch (e) {
        throw new Error("Failed to read file content.", { cause: e });
    }

    if (!text.trim()) {
        throw new Error("File content is empty.");
    }

    let data: any;
    try {
        data = JSON.parse(text);
    } catch (e) {
        throw new Error("File contains invalid JSON syntax.", { cause: e });
    }

    if (!data || typeof data !== 'object' || Array.isArray(data)) {
        throw new Error("Invalid PSS format: Root must be a JSON object.");
    }

    const project = data as WorkflowProject;
    return parseWorkflowProject(project);
};

/**
 * Saves the project to a JSON file (browser download).
 */
export const saveProjectToFile = (processes: ProcessDefinition[], fileName: string = "workflow_project") => {
    const project = generateWorkflowProject(processes, fileName);
    const jsonString = JSON.stringify(project, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.bpjs`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};
